import {put, takeLatest, call} from 'redux-saga/effects';
import {toast} from 'react-toastify';
import {get} from 'lodash';
import {transformParentEntities} from '@app/utils/helpers';
import {PHONE_DIAL_CODE} from '@app/utils/constants';
import {sagaActions as layoutSagaActions} from '@app/modules/layouts/redux/saga';
import {
  createEntityRequest,
  createEntitySuccess,
  createEntityError,
  fetchParentEntitiesRequest,
  fetchParentEntitiesSuccess,
  fetchParentEntitiesError,
  fetchEntityRequest,
  fetchEntitySuccess,
  fetchEntityError,
  updateEntityRequest,
  updateEntitySuccess,
  updateEntityError,
  fetchEntitySummaryRequest,
  fetchEntitySummarySuccess,
  fetchEntitySummaryError,
  fetchEntityActivitiesRequest,
  fetchEntityActivitiesSuccess,
  fetchEntityActivitiesError,
  fetchEntityReportersRequest,
  fetchEntityReportersSuccess,
  fetchEntityReportersError,
  sendReporterInviteRequest,
  sendReporterInviteSuccess,
  sendReporterInviteError,
} from './index';
import {
  createEntity,
  fetchParentEntities,
  fetchEntity,
  updateEntity,
  fetchEntitySummary,
  fetchEntityActivities,
  fetchEntityReporters,
  sendReporterInvite,
} from './api';

export const sagaActions = {
  CREATE_ENTITY: 'CREATE_ENTITY',
  FETCH_PARENT_ENTITIES: 'FETCH_PARENT_ENTITIES',
  FETCH_ENTITY: 'FETCH_ENTITY',
  UPDATE_ENTITY: 'UPDATE_ENTITY',
  FETCH_ENTITY_SUMMARY: 'FETCH_ENTITY_SUMMARY',
  FETCH_ENTITY_ACTIVITIES: 'FETCH_ENTITY_ACTIVITIES',
  FETCH_ENTITY_REPORTERS: 'FETCH_ENTITY_REPORTERS',
  SEND_REPORTER_INVITE: 'SEND_REPORTER_INVITE',
};

function* createEntitySaga({payload}) {
  const {values, history} = payload;
  try {
    yield put(createEntityRequest());
    const result = yield call(createEntity, {
      corporateId:
        values.parentEntity.parentEntity > 0
          ? values.parentEntity.parentEntity
          : values.parentEntity.value,
      entityType: values.entityType.value,
      parentEntity: values.parentEntity.value,
      parentEntityType: values.parentEntity.type,
      relationToParent: values.relationToParentEntity.value,
      name: values.entityName,
      reporterName: values.reporterName,
      reporterEmail: values.reporterEmail,
      reporterPhone: `${PHONE_DIAL_CODE}${values.reporterPhone}`,
      assignMeReporter: values.assignMeReporter.length > 0 ? true : false,
    });
    if (!result.error) {
      toast.success(result.message);
      yield put(createEntitySuccess(result.data));
      if (result.data.type === 'Facility') {
        history.push(`/facilities/${result.data.id}`);
      } else if (result.data.type === 'Subsidiary') {
        history.push(`/subsidiaries/${result.data.id}`);
      }
      // refresh corporate structure also
      yield put({
        type: layoutSagaActions.FETCH_CORPORATE_STRUCTURE,
        payload: {},
      });
    } else {
      toast.error(result.error);
      yield put(createEntityError(result.error));
    }
  } catch (e) {
    const message = get(e, 'response.message', '');
    if (message) {
      toast.error(message);
      yield put(createEntityError(message));
    } else {
      toast.error(e.error);
      yield put(createEntityError(e.error));
    }
  }
}

function* fetchParentEntitiesSaga({payload}) {
  const {values} = payload;
  try {
    yield put(fetchParentEntitiesRequest());
    const result = yield call(fetchParentEntities, values);
    if (!result.error) {
      const data = yield call(transformParentEntities, result.data);
      yield put(fetchParentEntitiesSuccess(data));
    } else {
      yield put(fetchParentEntitiesSuccess([]));
    }
  } catch (e) {
    const message = get(e, 'response.message', '');
    if (message) {
      toast.error(message);
      yield put(fetchParentEntitiesError(message));
    } else {
      toast.error(e.error);
      yield put(fetchParentEntitiesError(e.error));
    }
  }
}

function* fetchEntitySaga({payload}) {
  const {values} = payload;
  try {
    yield put(fetchEntityRequest());
    const result = yield call(fetchEntity, values);
    if (!result.error) {
      yield put(fetchEntitySuccess(result.data));
    } else {
      toast.error(result.error);
      yield put(fetchEntityError(result.error));
    }
  } catch (e) {
    const message = get(e, 'response.message', '');
    if (message) {
      toast.error(message);
      yield put(fetchEntityError(message));
    } else {
      toast.error(e.error);
      yield put(fetchEntityError(e.error));
    }
  }
}

function* updateEntitySaga({payload}) {
  const {values} = payload;
  try {
    yield put(updateEntityRequest());
    const result = yield call(updateEntity, {
      entityId: values.entityId,
      corporateId:
        values.parentEntity.parentEntity > 0
          ? values.parentEntity.parentEntity
          : values.parentEntity.value,
      entityType: values.entityType.value,
      parentEntity: values.parentEntity.value,
      parentEntityType: values.parentEntity.type,
      relationToParent: values.relationToParentEntity.value,
      name: values.entityName,
      entityStatus: values.entityStatus.value,
      ecoActivityCode: values.ecoActivityCode || 0,
      commercialRegistration: values.commercialRegistration,
      tradingName: values.tradingName,
      address1: values.address1,
      address2: values.address2,
      address3: values.address3,
      city: values.city,
      country: values.country.value,
      postalCode: values.postalCode,
    });
    if (!result.error) {
      toast.success(result.message);
      yield put(updateEntitySuccess(result.data));
      // refresh corporate structure also
      yield put({
        type: layoutSagaActions.FETCH_CORPORATE_STRUCTURE,
        payload: {},
      });
    } else {
      toast.error(result.error);
      yield put(updateEntityError(result.error));
    }
  } catch (e) {
    const message = get(e, 'response.message', '');
    if (message) {
      toast.error(message);
      yield put(updateEntityError(message));
    } else {
      toast.error(e.error);
      yield put(updateEntityError(e.error));
    }
  }
}

function* fetchEntitySummarySaga({payload}) {
  try {
    yield put(fetchEntitySummaryRequest());
    const result = yield call(fetchEntitySummary, payload);
    if (!result.error) {
      yield put(fetchEntitySummarySuccess(result.data));
    } else {
      toast.error(result.error);
      yield put(fetchEntitySummaryError(result.error));
    }
  } catch (e) {
    const message = get(e, 'response.message', '');
    if (message) {
      toast.error(message);
      yield put(fetchEntitySummaryError(message));
    } else {
      toast.error(e.error);
      yield put(fetchEntitySummaryError(e.error));
    }
  }
}

function* fetchEntityActivitiesSaga({payload}) {
  try {
    yield put(fetchEntityActivitiesRequest());
    const result = yield call(fetchEntityActivities, payload);
    if (!result.error) {
      yield put(fetchEntityActivitiesSuccess(result.data));
    } else {
      toast.error(result.error);
      yield put(fetchEntityActivitiesError(result.error));
    }
  } catch (e) {
    const message = get(e, 'response.message', '');
    if (message) {
      toast.error(message);
      yield put(fetchEntityActivitiesError(message));
    } else {
      toast.error(e.error);
      yield put(fetchEntityActivitiesError(e.error));
    }
  }
}

function* fetchEntityReportersSaga({payload}) {
  const {entityId, values} = payload;
  try {
    yield put(fetchEntityReportersRequest());
    const result = yield call(fetchEntityReporters, entityId, values);
    if (!result.error) {
      yield put(
        fetchEntityReportersSuccess({
          data: result.data.data,
          page: values.page,
          pageSize: values.pageSize,
          totalPages: result.data.totalPages,
          totalRecords: result.data.totalRecords,
          sortOn: '',
          sortDir: '',
          entityId,
        })
      );
    } else if (result.error) {
      toast.error(result.error);
      yield put(
        fetchEntityReportersSuccess({
          data: [],
          page: values.page,
          pageSize: values.pageSize,
          totalPages: 1,
          totalRecords: 0,
          sortOn: '',
          sortDir: '',
          entityId: 0,
        })
      );
    }
  } catch (e) {
    const message = get(e, 'response.message', '');
    if (message) {
      toast.error(message);
      yield put(fetchEntityReportersError(message));
    } else {
      toast.error(e.error);
      yield put(fetchEntityReportersError(e.error));
    }
  }
}

function* sendReporterInviteSaga({payload}) {
  const {entityId, values} = payload;
  try {
    yield put(sendReporterInviteRequest());
    const result = yield call(sendReporterInvite, entityId, {
      ...values,
      reporterPhone: `${PHONE_DIAL_CODE}${values.reporterPhone}`,
    });
    if (!result.error) {
      toast.success(result.message);
      yield put(sendReporterInviteSuccess(result));
      yield put({
        type: sagaActions.FETCH_ENTITY_REPORTERS,
        payload: {
          entityId,
          values: {
            page: 1,
            pageSize: 20,
            totalCount: 0,
            sortOn: 'createdOn',
            sortDir: 'DESC',
          },
        },
      });
    } else {
      toast.error(result.error);
      yield put(sendReporterInviteError(result.error));
    }
  } catch (e) {
    const message = get(e, 'response.message', '');
    if (message) {
      toast.error(message);
      yield put(sendReporterInviteError(message));
    } else {
      toast.error(e.error);
      yield put(sendReporterInviteError(e.error));
    }
  }
}

function* rootSagas() {
  yield takeLatest(sagaActions.CREATE_ENTITY, createEntitySaga);
  yield takeLatest(sagaActions.FETCH_PARENT_ENTITIES, fetchParentEntitiesSaga);
  yield takeLatest(sagaActions.FETCH_ENTITY, fetchEntitySaga);
  yield takeLatest(sagaActions.UPDATE_ENTITY, updateEntitySaga);
  yield takeLatest(sagaActions.FETCH_ENTITY_SUMMARY, fetchEntitySummarySaga);
  yield takeLatest(
    sagaActions.FETCH_ENTITY_ACTIVITIES,
    fetchEntityActivitiesSaga
  );
  yield takeLatest(
    sagaActions.FETCH_ENTITY_REPORTERS,
    fetchEntityReportersSaga
  );

  yield takeLatest(sagaActions.SEND_REPORTER_INVITE, sendReporterInviteSaga);
}

export default rootSagas;
